import { Autocomplete, FormControl, InputAdornment, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import PropTypes from 'prop-types';
import React from 'react';

import { CLIENT_CUSTOM_FIELD_ID, getAuthImportFieldLabel } from './importAuthFiles.tableDefinitions';

const nullableControlValue = value => (value === null || value === undefined ? '' : value);

// The API stores override values in an NVARCHAR(500) column
const OVERRIDE_VALUE_MAX_LENGTH = 500;
const EMPTY_OVERRIDE_LABEL = '-- Select a value --';

export const AUTH_UPDATE_IMPORT_DATA_TYPES = {
  BOOLEAN: 'Boolean',
  DATE: 'Date',
  DIVISION_CODE: 'Division Code',
  ENUMERATION: 'Enumeration',
  INTERNAL_USER: 'Internal User Email',
  MONEY: 'Money',
  POLICY_CODE: 'Policy Code',
  TEXT: 'Text',
};

// Values are matched by title, and the authorization details cards only offer active values
export const AUTH_UPDATE_IMPORT_ENUMERATIONS = {
  AUTHORIZATION_FILE_TYPES: { label: 'File Type', storeKey: 'authorizationFileTypes' },
  MARITAL_STATUSES: { label: 'Marital Status', storeKey: 'maritalStatuses' },
  MOVE_REASONS: { label: 'Move Reason', storeKey: 'moveReasons' },
  MOVE_TYPES: { label: 'Move Type', storeKey: 'moveTypes' },
  OWNERSHIP_TYPES: { label: 'Ownership Type', storeKey: 'ownershipTypes' },
  TAX_FILING_STATUSES: { label: 'Tax Filing Status', storeKey: 'taxFilingStatuses' },
  TEAMS: { label: 'Team', storeKey: 'teams', isSortedByTitle: true },
};

const OVERRIDE_CONTROL_TYPES = {
  BOOLEAN: 'boolean',
  DATE: 'date',
  ENUMERATION: 'enumeration',
  INTERNAL_USER: 'internalUser',
  LIST: 'list',
  NONE: 'none',
  TEXT: 'text',
};

const createAuthUpdateImportField = (fieldId, fieldName, fieldKey, dataType = AUTH_UPDATE_IMPORT_DATA_TYPES.TEXT, options = {}) => ({
  id: fieldId,
  fieldId,
  fieldName,
  fieldKey,
  dataType,
  enumeration: options.enumeration ?? null,
  isSystemRequired: options.isSystemRequired ?? false,
  maxLength: options.maxLength ?? OVERRIDE_VALUE_MAX_LENGTH,
  position: null,
  positionError: null,
  overrideValue: null,
});

const { BOOLEAN, DATE, DIVISION_CODE, ENUMERATION, INTERNAL_USER, MONEY, POLICY_CODE, TEXT } = AUTH_UPDATE_IMPORT_DATA_TYPES;
const {
  AUTHORIZATION_FILE_TYPES,
  MARITAL_STATUSES,
  MOVE_REASONS,
  MOVE_TYPES,
  OWNERSHIP_TYPES,
  TAX_FILING_STATUSES,
  TEAMS,
} = AUTH_UPDATE_IMPORT_ENUMERATIONS;

// Field ids match ImportTool.Field. Sections and fields follow the order of the authorization details cards, except that
// Team leads the Team section.
export const AUTH_UPDATE_IMPORT_SECTIONS = [
  {
    sectionKey: 'authDetails',
    sectionName: 'Main Auth Details',
    fields: [
      // Matches each row to the authorization it updates, so it always comes from a column
      createAuthUpdateImportField(1, 'File Number', 'fileNumber', TEXT, { isSystemRequired: true }),
      createAuthUpdateImportField(78, 'Division', 'divisionId', DIVISION_CODE),
      createAuthUpdateImportField(80, 'VIP', 'isVIP', BOOLEAN),
      createAuthUpdateImportField(81, 'Confidential File', 'isConfidentialFile', BOOLEAN),
      createAuthUpdateImportField(79, 'Policy', 'relocationPolicyId', POLICY_CODE),
      createAuthUpdateImportField(82, 'Move Type', 'moveTypeId', ENUMERATION, { enumeration: MOVE_TYPES }),
      createAuthUpdateImportField(83, 'Move Reason', 'moveReasonId', ENUMERATION, { enumeration: MOVE_REASONS }),
      createAuthUpdateImportField(84, 'File Type', 'authorizationFileTypeId', ENUMERATION, { enumeration: AUTHORIZATION_FILE_TYPES }),
      createAuthUpdateImportField(85, 'Repayment Agreement Sent', 'repayAgreementSent', DATE),
      createAuthUpdateImportField(86, 'Repayment Agreement Received', 'repayAgreementReceived', DATE),
      createAuthUpdateImportField(87, 'Origin Status', 'departureOwnershipTypeId', ENUMERATION, { enumeration: OWNERSHIP_TYPES }),
      createAuthUpdateImportField(88, 'Destination Status', 'destinationOwnershipTypeId', ENUMERATION, { enumeration: OWNERSHIP_TYPES }),
      createAuthUpdateImportField(89, 'Move CAP', 'moveCap', MONEY),
      createAuthUpdateImportField(90, 'Init Date', 'initiationDate', DATE),
      createAuthUpdateImportField(91, 'Init Call Date', 'initCallDate', DATE),
      createAuthUpdateImportField(92, 'Job Start Date', 'newJobStartDate', DATE),
      createAuthUpdateImportField(93, 'Estimated Start Date', 'estimatedStartDate', DATE),
      createAuthUpdateImportField(94, 'Estimated End Date', 'estimatedEndDate', DATE),
    ],
  },
  {
    sectionKey: 'team',
    sectionName: 'Team',
    fields: [
      createAuthUpdateImportField(95, 'Team', 'teamId', ENUMERATION, { enumeration: TEAMS }),
      createAuthUpdateImportField(96, 'Counselor', 'counselor', INTERNAL_USER),
      createAuthUpdateImportField(97, 'Expense Analyst', 'expenseAnalyst', INTERNAL_USER),
      createAuthUpdateImportField(98, 'RC Reports To', 'reportsTo', INTERNAL_USER),
      createAuthUpdateImportField(99, 'DSD', 'dsd', INTERNAL_USER),
      createAuthUpdateImportField(100, 'CSS', 'css', INTERNAL_USER),
      createAuthUpdateImportField(101, 'CSM', 'csm', INTERNAL_USER),
      createAuthUpdateImportField(102, 'Inventory SP', 'inventorySP', INTERNAL_USER),
      createAuthUpdateImportField(103, 'Payroll SP', 'payrollSP', INTERNAL_USER),
      createAuthUpdateImportField(104, 'Global CA', 'globalCA', INTERNAL_USER),
    ],
  },
  {
    sectionKey: 'finance',
    sectionName: 'Finance',
    fields: [
      createAuthUpdateImportField(105, 'Short Term Assignment', 'isShortTermAssignment', BOOLEAN),
      createAuthUpdateImportField(106, 'Title', 'jobTitle', TEXT, { maxLength: 100 }),
      createAuthUpdateImportField(107, 'Department', 'department', TEXT, { maxLength: 100 }),
      createAuthUpdateImportField(108, 'Marital Status', 'maritalStatusId', ENUMERATION, { enumeration: MARITAL_STATUSES }),
      createAuthUpdateImportField(109, 'Tax Filing Status', 'taxFilingStatusId', ENUMERATION, { enumeration: TAX_FILING_STATUSES }),
      createAuthUpdateImportField(110, 'Relocation Allowance Amount', 'relocationAllowanceAmount', MONEY),
      createAuthUpdateImportField(111, 'Relocation Payment Date', 'relocationPaymentDate', DATE),
      createAuthUpdateImportField(112, 'Spousal Assistance Estimated Date', 'spousalAssistanceEstimatedDate', DATE),
    ],
  },
  {
    // Fields come from the template's client at runtime (see createClientCustomField)
    sectionKey: 'customFields',
    sectionName: 'Custom Fields',
    isClientCustomFieldSection: true,
    fields: [],
  },
];

export const AUTH_UPDATE_IMPORT_FIELDS = AUTH_UPDATE_IMPORT_SECTIONS.reduce((fields, section) => [
  ...fields,
  ...section.fields.map(field => ({
    ...field,
    sectionKey: section.sectionKey,
    sectionName: section.sectionName,
  })),
], []);

const AUTH_UPDATE_IMPORT_FIELDS_BY_FIELD_ID = new Map(AUTH_UPDATE_IMPORT_FIELDS.map(field => [String(field.fieldId), field]));

export const getAuthUpdateImportFieldByFieldId = fieldId => AUTH_UPDATE_IMPORT_FIELDS_BY_FIELD_ID.get(String(fieldId));

const AUTH_UPDATE_IMPORT_FIELD_ORDER = new Map(AUTH_UPDATE_IMPORT_FIELDS.map((field, index) => [String(field.fieldId), index]));

export const getAuthUpdateImportFieldOrder = fieldId => AUTH_UPDATE_IMPORT_FIELD_ORDER.get(String(fieldId)) ?? Number.MAX_SAFE_INTEGER;

export const getInitialAuthUpdateImportMappings = () => AUTH_UPDATE_IMPORT_FIELDS.reduce((mappings, field) => ({
  ...mappings,
  [field.fieldKey]: {
    ...field,
    position: null,
    positionError: null,
    overrideValue: null,
  },
}), {});

export const getAdditionalFields = section => section.fields.filter(field => !field.isSystemRequired);

// Client custom field entries only hold mapping state, so rows merge the field definition with whatever mapping state exists
export const getVisibleSectionRows = (section, selectedAdditionalFieldKeys, mappingsByFieldKey) => section.fields.filter(field => (
  field.isSystemRequired || selectedAdditionalFieldKeys.includes(field.fieldKey)
)).map(field => ({
  ...field,
  ...mappingsByFieldKey[field.fieldKey],
}));

export const hasPosition = row => row.position !== null && row.position !== undefined && row.position !== '';

export const hasOverrideValue = row => row.overrideValue !== null && row.overrideValue !== undefined && row.overrideValue !== '';

export const getDataTypeLabel = row => (row.enumeration ? row.enumeration.label : row.dataType);

const isSameText = (firstValue, secondValue) => Boolean(firstValue && secondValue)
  && String(firstValue).trim().toLowerCase() === String(secondValue).trim().toLowerCase();

const getOverrideControlType = row => {
  if (row.isSystemRequired) return OVERRIDE_CONTROL_TYPES.NONE;

  // Client custom fields use the client's field type titles
  if (Number(row.fieldId) === CLIENT_CUSTOM_FIELD_ID) {
    switch (row.dataType?.trim().toLowerCase()) {
      case 'boolean':
        return OVERRIDE_CONTROL_TYPES.BOOLEAN;
      case 'date':
        return OVERRIDE_CONTROL_TYPES.DATE;
      case 'list':
        return OVERRIDE_CONTROL_TYPES.LIST;
      default:
        return OVERRIDE_CONTROL_TYPES.TEXT;
    }
  }

  switch (row.dataType) {
    case BOOLEAN:
      return OVERRIDE_CONTROL_TYPES.BOOLEAN;
    case DATE:
      return OVERRIDE_CONTROL_TYPES.DATE;
    case ENUMERATION:
      return OVERRIDE_CONTROL_TYPES.ENUMERATION;
    case INTERNAL_USER:
      return OVERRIDE_CONTROL_TYPES.INTERNAL_USER;
    default:
      return OVERRIDE_CONTROL_TYPES.TEXT;
  }
};

export const getCustomFieldListValues = customField => (
  typeof customField?.fieldListValues === 'string'
    ? customField.fieldListValues.split(';').map(value => value.trim()).filter(Boolean)
    : []
);

const getActiveEnumerationTitles = (enumerations, enumeration) => {
  const values = enumerations?.[enumeration?.storeKey] ?? enumerations?.allEnums?.[enumeration?.storeKey] ?? [];

  const titles = values.filter(value => value.isActive !== false).map(value => value.title).filter(Boolean);

  // Numeric collation keeps "Team 10" after "Team 9"
  return enumeration?.isSortedByTitle
    ? [...titles].sort((firstTitle, secondTitle) => firstTitle.localeCompare(secondTitle, undefined, { numeric: true }))
    : titles;
};

// A saved value that is no longer offered (an inactive value, for example) stays selectable so it is not silently dropped
const getSelectOptions = (titles, value) => (
  value && !titles.some(title => title === value) ? [value, ...titles] : titles
);

export const getInternalUserLabel = user => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name ? `${name} (${user.emailAddress})` : user?.emailAddress ?? '';
};

export const getInternalUserOptions = internalUsers => (internalUsers ?? [])
  .filter(user => user.emailAddress)
  .sort((firstUser, secondUser) => getInternalUserLabel(firstUser).localeCompare(getInternalUserLabel(secondUser)));

const findInternalUserByEmail = (internalUsers, emailAddress) => internalUsers?.find(user => isSameText(user.emailAddress, emailAddress));

export const formatOverrideValue = (row, internalUsers) => {
  if (!hasOverrideValue(row)) return '';

  switch (getOverrideControlType(row)) {
    case OVERRIDE_CONTROL_TYPES.BOOLEAN:
      if (row.overrideValue === 'true') return 'Yes';
      if (row.overrideValue === 'false') return 'No';
      return row.overrideValue;
    case OVERRIDE_CONTROL_TYPES.INTERNAL_USER: {
      const user = findInternalUserByEmail(internalUsers, row.overrideValue);
      return user ? getInternalUserLabel(user) : row.overrideValue;
    }
    default:
      return String(row.overrideValue);
  }
};

// A column position clears the override, which shows the same "no value" option as an override that was never set
const getSelectRenderValue = value => (value === '' ? EMPTY_OVERRIDE_LABEL : value);

const OverrideSelect = props => {
  const { onChange, options, row } = props;

  return (
    <FormControl fullWidth size="small">
      <Select
        displayEmpty
        value={hasPosition(row) ? '' : nullableControlValue(row.overrideValue)}
        renderValue={value => getSelectRenderValue(options.find(option => option.value === value)?.label ?? value)}
        onChange={event => onChange(event.target.value || null)}
      >
        <MenuItem value="">{EMPTY_OVERRIDE_LABEL}</MenuItem>
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

OverrideSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  row: PropTypes.object.isRequired,
};

const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const getTitleOptions = titles => titles.map(title => ({ label: title, value: title }));

const InternalUserOverrideControl = props => {
  const { internalUserOptions, onChange, row } = props;
  const overrideValue = hasPosition(row) ? null : row.overrideValue;
  // An email that no longer matches a user is kept, so it shows (and fails the dry run) instead of disappearing
  const selectedUser = overrideValue
    ? findInternalUserByEmail(internalUserOptions, overrideValue) ?? { emailAddress: overrideValue }
    : null;

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={internalUserOptions}
      value={selectedUser}
      getOptionLabel={getInternalUserLabel}
      isOptionEqualToValue={(option, value) => isSameText(option.emailAddress, value.emailAddress)}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option.internalUserId ?? option.emailAddress}>
          {getInternalUserLabel(option)}
        </li>
      )}
      renderInput={params => <TextField {...params} placeholder="Select internal user" />}
      onChange={(event, user) => onChange(user?.emailAddress ?? null)}
    />
  );
};

InternalUserOverrideControl.propTypes = {
  internalUserOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
};

const OverrideValueControl = props => {
  const { enumerations, internalUserOptions, onOverrideValueChange, row } = props;
  const handleChange = value => onOverrideValueChange(row.fieldKey, value);
  const controlValue = hasPosition(row) ? '' : nullableControlValue(row.overrideValue);

  switch (getOverrideControlType(row)) {
    case OVERRIDE_CONTROL_TYPES.NONE:
      return <TextField fullWidth size="small" disabled placeholder="Column only" />;
    case OVERRIDE_CONTROL_TYPES.BOOLEAN:
      return <OverrideSelect row={row} options={BOOLEAN_OPTIONS} onChange={handleChange} />;
    case OVERRIDE_CONTROL_TYPES.ENUMERATION:
      return (
        <OverrideSelect
          row={row}
          options={getTitleOptions(getSelectOptions(getActiveEnumerationTitles(enumerations, row.enumeration), row.overrideValue))}
          onChange={handleChange}
        />
      );
    case OVERRIDE_CONTROL_TYPES.LIST:
      return (
        <OverrideSelect
          row={row}
          options={getTitleOptions(getSelectOptions(row.listValues ?? [], row.overrideValue))}
          onChange={handleChange}
        />
      );
    case OVERRIDE_CONTROL_TYPES.INTERNAL_USER:
      return <InternalUserOverrideControl row={row} internalUserOptions={internalUserOptions} onChange={handleChange} />;
    case OVERRIDE_CONTROL_TYPES.DATE:
      return (
        <TextField
          fullWidth
          size="small"
          type="date"
          value={controlValue}
          onChange={event => handleChange(event.target.value || null)}
        />
      );
    default:
      return (
        <TextField
          fullWidth
          size="small"
          value={controlValue}
          inputProps={{ maxLength: row.maxLength ?? OVERRIDE_VALUE_MAX_LENGTH }}
          onChange={event => handleChange(event.target.value || null)}
        />
      );
  }
};

OverrideValueControl.propTypes = {
  enumerations: PropTypes.object,
  internalUserOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOverrideValueChange: PropTypes.func.isRequired,
  row: PropTypes.shape({
    dataType: PropTypes.string,
    enumeration: PropTypes.object,
    fieldId: PropTypes.number,
    fieldKey: PropTypes.string.isRequired,
    isSystemRequired: PropTypes.bool,
    listValues: PropTypes.arrayOf(PropTypes.string),
    maxLength: PropTypes.number,
    overrideValue: PropTypes.string,
    position: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

OverrideValueControl.defaultProps = {
  enumerations: null,
};

const FieldNameLabel = props => {
  const { row } = props;

  return (
    <Typography variant="body2" color={row.isUnavailable ? 'error' : 'inherit'}>
      {getAuthImportFieldLabel(row)}
      {row.isSystemRequired && (
        <Typography component="span" color="error"> *</Typography>
      )}
    </Typography>
  );
};

FieldNameLabel.propTypes = {
  row: PropTypes.object.isRequired,
};

export const getAuthUpdateImportColumnMappings = props => {
  const { enumerations, internalUserOptions, onOverrideValueChange, onPositionChange } = props;

  return [
    {
      accessorKey: 'fieldName',
      header: 'Field Name',
      Cell: ({ row }) => <FieldNameLabel row={row.original} />,
    },
    {
      accessorKey: 'position',
      header: 'Position',
      Cell: ({ row }) => (
        <TextField
          error={Boolean(row.original.positionError)}
          fullWidth
          size="small"
          value={nullableControlValue(row.original.position)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            endAdornment: row.original.positionError ? (
              <InputAdornment position="end">
                <Tooltip title={row.original.positionError}>
                  <ErrorOutline color="error" fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          onChange={event => onPositionChange(row.original.fieldKey, event.target.value)}
        />
      ),
    },
    {
      accessorKey: 'overrideValue',
      header: 'Override Value',
      Cell: ({ row }) => (
        <OverrideValueControl
          row={row.original}
          enumerations={enumerations}
          internalUserOptions={internalUserOptions}
          onOverrideValueChange={onOverrideValueChange}
        />
      ),
    },
    {
      accessorKey: 'dataType',
      header: 'Data Type',
      Cell: ({ row }) => (
        <Typography variant="body2">{getDataTypeLabel(row.original)}</Typography>
      ),
    },
  ];
};

getAuthUpdateImportColumnMappings.propTypes = {
  enumerations: PropTypes.object,
  internalUserOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOverrideValueChange: PropTypes.func.isRequired,
  onPositionChange: PropTypes.func.isRequired,
};

export const getAuthUpdateImportTemplateDisplayColumns = internalUsers => [
  {
    accessorKey: 'fieldName',
    header: 'Field Name',
    enableSorting: false,
    Cell: ({ row }) => {
      if (row.original.isSectionHeader) {
        return (
          <Typography variant="subtitle2">
            {row.original.sectionName}
          </Typography>
        );
      }

      return row.original.isUnavailable ? (
        <Typography variant="body2" color="error">
          {getAuthImportFieldLabel(row.original)}
        </Typography>
      ) : row.original.fieldName;
    },
  },
  {
    accessorKey: 'position',
    header: 'Position',
    enableSorting: false,
    Cell: ({ row }) => (row.original.isSectionHeader ? '' : nullableControlValue(row.original.position)),
  },
  {
    accessorKey: 'overrideValue',
    header: 'Override Value',
    enableSorting: false,
    Cell: ({ row }) => (row.original.isSectionHeader ? '' : formatOverrideValue(row.original, internalUsers)),
  },
  {
    accessorKey: 'dataType',
    header: 'Data Type',
    enableSorting: false,
    Cell: ({ row }) => (row.original.isSectionHeader ? '' : getDataTypeLabel(row.original)),
  },
];
